define([
	"dojo/_base/declare",
	"dojo/store/Memory",
	"underscore",
	"csi",
	"csi/def",
	"csi/html",
	"csi/model/customView",
	"csi/prefs",
	"csi/core/Key",
	"csi/reflection",
	"csi/service/viewService",
	"csi/state/datasetup",
	"csi/table/Widget",
	"csi/view",
	"Export/ExportTable/Widget"
], function(declare, Memory, _, csi, def, html, {ATTR_PATH_HINTS, getCustomViewContainerKeyForType, isCustomViewContainerTypeKey},
	prefs, Key, reflection, {getClassEmptyPropertyView, getClassDefaultTableView}, {dataSetupStore}, Widget, csiView, ExportTable) {

	const SPECIAL = {
		"TestsBySection|TestRunSection": /\w*TestsBySection\|TestRunSection/
	};

	const _PageSizeStore = new Memory({
		data: [
			{name: "20", id: "20"},
			{name: "100", id: "100"},
			{name: "500", id: "500"},
			{name: "1000", id: "1000"}
		]
	});

	const TYPE_PRODUCT_PRESENTATION_ATTRIBUTE_SETTINGS = "ProductPresentationAttributeSettings";

	/**
	 * Return key to use for caching view (definition) in combination with custom view target class, if provided
	 * @param {string} key
	 * @param {Node} [cv]
	 * @return {string}
	 */
	function _getViewDefinitionCacheKey(key, cv) {
		return cv ? (key + "&" + cv.TargetClass) : key;
	}

	return declare(Widget, {
		ERROR_HEADING_KEY: "Heading key changed",
		ERROR_NESTED_KEY: "Nest view key changed",
		ERROR_KEY: "View key changed",
		ERROR_MISSING: "View no longer exists",
		ERROR_ORPHAN: "Custom view is orphan",
		ERROR_PREFS: "View no longer supports preferences",
		ERROR_TARGET: "View missing target class",

		nRowsPerTimeout: 5,
		queryLimit: 0,
		querySizeStore: _PageSizeStore,
		querySizeStoreForCustomView: _PageSizeStore,

		mapViews: {},
		mapViewDefs: {},

		_mapKeyReports: null,
		_mapMoves: null,
		_mapNested: null,
		_mapOrphans: null,
		_mapTargetClasses: null,

		postMixInProperties: function() {
			this.resetMigration();
			this.inherited(arguments);
		},

		/**
		 * Add report of issues with custom view keys for given target class
		 * @param {C8NodeType} targetClass 
		 * @param {CustomViewMigrationKeyReport} report 
		 */
		addReport(targetClass, report) {
			const existingReport = this._mapKeyReports[targetClass];
			if (existingReport) {
				existingReport.addReport(report);
			} else {
				this._mapKeyReports[targetClass] = report;
			}
		},

		errorNested: function(/*String*/viewKey, /*String[]*/values) {
			var obj = this._mapNested[viewKey];
			if (!obj) this._mapNested[viewKey] = obj = {};
			_.each(values, function(s) {obj[s] = true;});
		},

		moveView: function(/*String*/viewKey, /*String*/url) {
			this._mapMoves[viewKey] = url;
		},

		/**
		 * the map to store the CVs which need to populate the attribute - TargetClass
		 * @param {cnl} cnl 
		 * @param {string} targetType 
		 */
		specifyTargetClass(cnl, targetType) {
			this._mapTargetClasses[cnl] = targetType;
		},

		orphanView: function(/*String*/url) {
			this._mapOrphans[url] = true;
		},

		/**
		 * Render items in report
		 * @param {HTMLElement} element 
		 * @param {CustomViewMigrationKeyReport} report 
		 * @return {boolean}
		 */
		renderReport(element, report) {
			const {duplicates, results} = report;

			if (results.length) {
				for (const [section, items] of results) {
					const sectionDiv = html.create("div", {
						innerText: section,
						style: {
							marginBottom: "4px",
						},
					}, element);

					for (const {key, search, replace} of items) {
						// use red if key is being removed, green if replaced, orange if not sure
						const color = replace ? "green" : (search ? "orange" : "red");

						html.create("div", {
							innerText: key,
							style: {
								color,
							}
						}, sectionDiv);
					}
				}

				if (duplicates.length) {
					html.create("div", {
						innerText: "Duplicates - see console",
					}, element);
				}

				return true;
			}

			return false;
		},

		resetMigration: function() {
			this._mapKeyReports = {};
			this._mapMoves = {};
			this._mapNested = {};
			this._mapOrphans = {};
			this._mapTargetClasses = {};
		},

		/**
		 * Return custom view container key for given custom view
		 * @param {C8Node} cv
		 * @return {string | undefined}
		 */
		_getContainerKey(cv) {
			const urlContainer = cv.__Parent__;
			const container = this.cacheView.get(urlContainer);

			if (container?.$Type !== prefs.TYPE_CONTAINER) {
				// AggregateStoreGroup is now a potential parent of custom views; in that case,
				// just revert to using target class
				return getCustomViewContainerKeyForType(cv.TargetClass);
			}

			if (urlContainer) {
				return prefs.getContainerKey(urlContainer);
			}
		},

		_getNestedViewKeys: function(/*csi.view.Widget*/view) {
			var store = view && view.getNestedViewStore && view.getNestedViewStore();
			if (store) {
				return store.query().map(function(obj) {
					return store.getIdentity(obj);
				});
			}

			return null;
		},

		/**
		 * Resolve view for given view key
		 * @param {string} key
		 * @param {Node} [cv]
		 * @return {csi/view/Widget}
		 */
		_getViewDefinition: function(key, cv) {
			if (!key) {
				return null;
			}

			const cacheKey = _getViewDefinitionCacheKey(key, cv);
			var view = this.mapViews[cacheKey];
			if (!view) {
				const defView = this.mapViewDefs[cacheKey];
				if (defView) {
					// support for Export tables - controller class is not added to every __Export__ view, so add explicitly here
					if (defView.getId() == "__Export__") {
						defView.classController = ExportTable;
					}

					view = this.mapViews[cacheKey] = csiView.newView(defView, null, {customViewMigrationContainerKey: key});
				} else {
					return false;
				}
			}

			return view;
		},

		_getViewPaths: function(/*Context*/ctx) {
			// summary: determine unique paths used by view
			var cv = ctx.getNode();
			var view = this._getViewDefinition(this._getContainerKey(cv), cv);
			if (!view) return null;

			var headings = csi.transform(cv.Attributes, function(key) {
				var keyObj = new Key({dpkey: cv.Keys[key]});
				var spec = view.getHeadingSpec(keyObj);
				return spec ? spec.heading : null;
			});

			return _.uniq(def.getHeadingsDependencyPaths(headings));
		},

		/**
		 * extract TargetClass from CV node 
		 * @param {CustomView} cv 
		 * @param {string} key
		 * @return {string}
		 */
		extractTargetClassFromCV(cv, key) {
			let targetClass = cv.TargetClass;

			// we didn't specify the value of the TargetClass for ProductPresentationAttributeSettings on 7.4 , need to fix them accordingly
			if (key === TYPE_PRODUCT_PRESENTATION_ATTRIBUTE_SETTINGS && !targetClass) {
				const urlObjects = cv.$URL.split("/");
				targetClass = urlObjects[urlObjects.length - 1];
			}

			return targetClass;
		},

		/**
		 * Load view definition for given view key and custom view
		 * @param {string} key
		 * @param {Node} cv
		 * @param {boolean} [bSpecial]
		 * @return {View}
		 */
		_loadViewDefinition: function(key, cv, bSpecial) {
			if (!key) {
				return null;
			}

			const cacheKey = _getViewDefinitionCacheKey(key, cv);
			var obj = this.mapViewDefs;
			var defView = obj[cacheKey];
			if (defView === false) {
				return null;
			}

			if (!defView) {
				const container = this.cacheView.get(cv.__Parent__);
				var rc;

				if (key === prefs.ATTRIBUTE_CARDS_KEY || key === TYPE_PRODUCT_PRESENTATION_ATTRIBUTE_SETTINGS) {
					const targetClass = this.extractTargetClassFromCV(cv, key);

					// create property view per type for custom views used to specify cards
					// as for ProductPresentationAttributeSettings, we are getting any attributes that are in the domain object, the class default property view is probably good enough as it should include all attributes in the domain, plus more
					rc = reflection.getClass(targetClass);
					defView = getClassEmptyPropertyView(rc);
					defView.prefKey = key;
				} else if (isCustomViewContainerTypeKey(key)) {
					// check to see if data setup view - if so, then find view definition there rather than create one dynamically
					const [section, item] = dataSetupStore.getSectionAndItemForType(cv.TargetClass);

					if (item) {
						// use view for item
						defView = item.viewDefinition;
					} else {
						// create table view based on type and path hints - source class does not matter
						rc = reflection.getClass(cv.TargetClass);
						defView = getClassDefaultTableView(rc, null, container[ATTR_PATH_HINTS], key);
					}
				} else {
					var keyObj = def.decomposeViewKey(key);
					rc = reflection.getClass(keyObj.type);
					defView = def.getClassViewDef(rc, keyObj.id);

					if (!defView && !bSpecial) {
						var specialKey = csi.search(Object.keys(SPECIAL), function(skey) {
							return key.match(SPECIAL[skey]);
						});

						if (specialKey) {
							defView = this._loadViewDefinition(specialKey, cv, true);
						}
					} else if (defView) {
						// if a view is defined on a base class, then its Relationship may also target another base class (such as a Product's view of ProductColors).
						// When the view is instantiated with a node of a derived class, the target class of the Relationship is more concrete. That class is then 
						// assigned to the custom view's TargetClass. In such cases, the custom view migration tool should process using the custom view's TargetClass 
						// rather than the abstract target class derived from the model.
						const rel = defView.getPageRelationship();
						rc = defView.getRootClass();

						if (cv && rel && rc.abstract) {
							const rcViewTarget = rel.getTargetClass(rc);
							const rcCustomViewTarget = reflection.getClass(cv.TargetClass);

							if (!rcViewTarget || rcCustomViewTarget?.isDerivedFrom(rcViewTarget) || rcCustomViewTarget?.hasCommonBaseClassWith(rcViewTarget)) {
								rel.setType(cv.TargetClass);
							}
						}
					}
				}

				obj[cacheKey] = defView || false;
			}

			return defView;
		},

		_buildViewResults: function(/*QueryResult*/qrParent, /*DOMNode*/row, /*DOMNode*/rowNext, /*QueryResults*/results, /*Boolean*/bRefresh) {
			// summary: make sure to load all view classes needed before rendering table
			// TODO: Is this function even needed anymore?
			var a;

			results.forEach(function(node) {
				if (node.$Type != prefs.TYPE_VIEW) {
					node = this.cacheView.getNode(node.__Parent__);
				}

				if (node) {
					var key = this._getContainerKey(node);
					var defView = this._loadViewDefinition(key, node);
					if (defView) {
						// pre-load abstract view classes as well
						if (defView.m_rcRoot.abstract && !defView._debugAbstract) {
							if (a = defView.m_rcRoot.getConcreteClasses()) {
								var id = defView.getId();

								_.each(a, function(rc) {
									this._loadViewDefinition(def.composeViewKey(id, rc.getType()), node);
								}, this);
							}

							defView._debugAbstract = true;
						}
					}
				}
			}, this);

			return this.inherited(arguments);
		}
	});
});
